import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ShieldCheck, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { GFFrame } from "@/components/GFFrame";
import { api } from "@/lib/gfApi";

const PURPLE = "#4B006E";
const GOLD = "#D4AF37";

export default function CohortAssessment() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stage, setStage] = useState("consent"); // consent | quiz | done
  const [agree, setAgree] = useState(false);
  const [pid, setPid] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!sessionStorage.getItem("gf_cohort_profile") || !sessionStorage.getItem("gf_cohort")) {
      navigate("/cohort"); return;
    }
    api.get("/cohort/questions").then((r) => setData(r.data)).catch(() => toast.error("Gagal memuatkan soalan."));
  }, [navigate]);

  const startQuiz = async () => {
    if (!agree) { toast.error("Sila tandakan persetujuan untuk meneruskan."); return; }
    setBusy(true);
    try {
      const cohort = JSON.parse(sessionStorage.getItem("gf_cohort"));
      const profile = JSON.parse(sessionStorage.getItem("gf_cohort_profile"));
      const res = await api.post("/cohort/register", { code: cohort.access_code, ...profile, consent: true }).then((r) => r.data);
      setPid(res.participant_id);
      setStage("quiz");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mendaftar. Sila cuba lagi.");
    } finally { setBusy(false); }
  };

  if (!data) return <GFFrame><div className="flex min-h-screen items-center justify-center text-slate-400">Memuatkan…</div></GFFrame>;

  // ---- Consent (Ruang Selamat) ----
  if (stage === "consent") {
    return (
      <GFFrame testId="cohort-consent-page">
        <div className="flex min-h-screen flex-col px-6 pb-10 pt-8" style={{ background: PURPLE }}>
          <button onClick={() => navigate("/cohort/mula")} className="flex w-fit items-center gap-1 text-sm" style={{ color: "#C9B2D6" }}>
            <ArrowLeft className="h-4 w-4" /> Kembali
          </button>
          <div className="mt-6">
            <ShieldCheck className="h-8 w-8" style={{ color: GOLD }} />
            <h2 className="mt-3 font-heading text-3xl font-bold text-white">Ruang Selamat & Kerahsiaan</h2>
          </div>
          <div className="mt-5 space-y-3 text-sm leading-relaxed" style={{ color: "#DcCbE5" }}>
            <p>Growth Factor diwujudkan sebagai ruang pembangunan yang selamat, hormat dan tanpa penghakiman.</p>
            <p>Segala perkongsian akan dikendalikan secara sulit. Perkongsian peribadi anda tidak akan dilaporkan kepada organisasi dalam bentuk yang boleh mengenal pasti anda, kecuali dengan persetujuan anda atau apabila diperlukan atas sebab keselamatan atau kewajipan undang-undang.</p>
            <p>Organisasi hanya menerima dapatan keseluruhan (aggregated insights), corak umum dan cadangan pembangunan — bukan cerita peribadi atau jawapan individu.</p>
            <p>Anda tidak perlu berkongsi sesuatu yang anda tidak selesa. Had anda akan dihormati.</p>
          </div>
          <div className="mt-5 rounded-xl px-4 py-3 text-center text-sm font-bold" style={{ background: "rgba(240,199,80,0.15)", color: GOLD }}>
            “BAWA PULANG PEMBELAJARAN, BUKAN CERITA ORANG.”
          </div>
          <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-2xl bg-white/10 p-4">
            <input data-testid="cohort-consent-check" type="checkbox" checked={agree}
              onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 h-5 w-5 accent-[#D4AF37]" />
            <span className="text-sm text-white">Saya faham dan bersetuju untuk menghormati kerahsiaan serta privasi peserta lain sepanjang program ini.</span>
          </label>
          <div className="mt-auto pt-8">
            <button data-testid="cohort-consent-continue" onClick={startQuiz} disabled={busy || !agree}
              className="flex w-full items-center justify-center gap-3 rounded-full py-4 text-base font-bold transition-transform active:scale-[0.98] disabled:opacity-50"
              style={{ background: GOLD, color: PURPLE }}>
              {busy ? <><Loader2 className="h-5 w-5 animate-spin" /> Menyediakan…</> : "MULA PENILAIAN BEFORE"}
            </button>
          </div>
        </div>
      </GFFrame>
    );
  }

  // ---- Done ----
  if (stage === "done") {
    return (
      <GFFrame testId="cohort-done-page">
        <div className="flex min-h-screen flex-col items-center justify-center px-8 text-center">
          <CheckCircle2 className="h-16 w-16" style={{ color: PURPLE }} />
          <h2 className="mt-5 font-heading text-3xl font-bold" style={{ color: PURPLE }}>Terima kasih!</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">
            Respons Before Assessment anda telah direkodkan secara sulit. Dapatan akan digunakan
            secara agregat untuk menyesuaikan program pasukan anda.
          </p>
          <button onClick={() => navigate("/")} data-testid="cohort-done-home"
            className="mt-8 rounded-full px-6 py-3 text-sm font-bold text-white" style={{ background: PURPLE }}>
            Selesai
          </button>
        </div>
      </GFFrame>
    );
  }

  // ---- Quiz ----
  const total = data.total;
  const q = data.questions[idx];
  const current = answers[q.id];
  const progress = ((idx + (current ? 1 : 0)) / total) * 100;

  const choose = (v) => {
    const next = { ...answers, [q.id]: v };
    setAnswers(next);
    setTimeout(() => { idx < total - 1 ? setIdx(idx + 1) : finish(next); }, 200);
  };

  const finish = async (final) => {
    setBusy(true);
    try {
      const core = data.questions.filter((qq) => qq.scored).map((qq) => final[qq.id]);
      const base = data.questions.filter((qq) => !qq.scored).map((qq) => final[qq.id]);
      await api.post("/cohort/before", { participant_id: pid, answers: core, baseline: base });
      setStage("done");
    } catch (e) {
      setBusy(false);
      toast.error("Gagal menghantar. Sila cuba lagi.");
    }
  };

  return (
    <GFFrame testId="cohort-assessment-page">
      <div className="flex min-h-screen flex-col">
        <div className="px-6 pt-8">
          <div className="mb-3 flex items-center justify-between">
            <button onClick={() => (idx > 0 ? setIdx(idx - 1) : setStage("consent"))} className="text-sm text-slate-400" data-testid="cohort-q-back">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#7A2E9E" }}>{data.factor_labels[q.factor]}</span>
            <span className="text-sm font-bold text-slate-500">{idx + 1}/{total}</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <motion.div className="h-full rounded-full" style={{ background: GOLD }} animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
          </div>
        </div>
        <div className="flex flex-1 flex-col px-6 py-6">
          <AnimatePresence mode="wait">
            <motion.div key={q.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }} className="flex flex-1 flex-col">
              <div className="flex flex-1 items-center">
                <h2 className="font-heading text-2xl font-semibold leading-snug" style={{ color: PURPLE }}>{q.text}</h2>
              </div>
              <div className="space-y-2.5 pb-6">
                {data.scale.map((s) => {
                  const active = current === s.value;
                  return (
                    <button key={s.value} data-testid={`cohort-scale-${s.value}`} onClick={() => choose(s.value)}
                      className="flex w-full items-center gap-4 rounded-2xl border px-4 py-3.5 text-left transition-all active:scale-[0.99]"
                      style={active ? { background: PURPLE, borderColor: PURPLE, color: "#fff" } : { background: "#fff", borderColor: "#E4D6EC", color: PURPLE }}>
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold"
                        style={active ? { background: GOLD, color: PURPLE } : { background: "#F1E9F6", color: "#7A2E9E" }}>{s.value}</span>
                      <span className="text-[15px] font-medium">{s.label}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        {busy && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/85 backdrop-blur-sm">
            <Loader2 className="h-10 w-10 animate-spin" style={{ color: GOLD }} />
          </div>
        )}
      </div>
    </GFFrame>
  );
}
